import { useEffect, useMemo, useState } from 'react';
import { Calculator, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScopingCalculator } from './ScopingCalculator';
import { projectId } from '../utils/supabase/info';
import { initialSizingLines, sizingInputs, sizingTotals, type SizingLines, type SizingPricingModel, type SizingTemplate } from '../utils/sizingEstimate';
import { SizingLinesEditor } from './SizingLinesEditor';
import { FixedBidQuoteLines, MarkupPercentField, type MarkupInput } from './FixedBidMarkup';
import { firmDefaultMarkupPercent, fixedBidQuote } from '../utils/fixedBidMarkup';

/**
 * Sell › Sizing (ledger L05; step 5 of the simplification plan): the scope estimate before the
 * SOW. Pick a template, set the quantities, read the hours and the price at the firm's rate.
 * `ScopingCalculator` (never reachable before) does the arithmetic. A system template's scope
 * items come from its config items (one line per category, default hours) until a system
 * admin authors them in the Template Manager.
 */

interface SizingTabProps {
  accessToken: string;
  firmId: string;
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
export const DEFAULT_RATE = 150;

export function SizingTab({ accessToken, firmId }: SizingTabProps) {
  const [templates, setTemplates] = useState<SizingTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string>('');
  const [lines, setLines] = useState<SizingLines>({});
  const [hourlyRate, setHourlyRate] = useState<number>(DEFAULT_RATE);
  // Fixed bid or time and materials. A fixed bid starts at the hours × rate basis and is then
  // the firm's number; the margin against the basis is shown so the risk is visible.
  const [pricingModel, setPricingModel] = useState<SizingPricingModel>('time_and_materials');
  const [fixedPrice, setFixedPrice] = useState<number | ''>('');
  // The markup on a fixed bid (a contingency buffer), from the firm's default (Firm › Settings › Scoping).
  const [markupPercent, setMarkupPercent] = useState<MarkupInput>(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${accessToken}` };
        const [tRes, fRes] = await Promise.all([
          fetch(`${BASE}/sizing/templates`, { headers }),
          firmId ? fetch(`${BASE}/consulting-firms/${encodeURIComponent(firmId)}`, { headers }).catch(() => null) : Promise.resolve(null),
        ]);
        const tBody = await tRes.json().catch(() => ({}));
        if (!tRes.ok) throw new Error(tBody.error || `Request failed (${tRes.status})`);
        const list: SizingTemplate[] = Array.isArray(tBody.templates) ? tBody.templates : [];
        const fBody = fRes && fRes.ok ? await fRes.json().catch(() => ({})) : {};
        if (cancelled) return;
        setTemplates(list);
        const rate = Number(fBody?.firm?.defaultHourlyRate);
        if (Number.isFinite(rate) && rate > 0) setHourlyRate(rate);
        setMarkupPercent(firmDefaultMarkupPercent(fBody?.firm));
        if (list[0]) setTemplateId(list[0].id);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Could not load the templates');
        setTemplates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken, firmId]);

  const template = useMemo(() => templates?.find((t) => t.id === templateId) ?? null, [templates, templateId]);

  useEffect(() => {
    if (!template) return;
    setLines(initialSizingLines(template));
    setFixedPrice('');
  }, [template]);

  const scopeItems = useMemo(() => (template ? sizingInputs(template, lines) : []), [template, lines]);
  const totals = useMemo(() => sizingTotals(scopeItems), [scopeItems]);
  const basis = Math.round(totals.hoursEstimate * hourlyRate);
  const bid = fixedPrice === '' ? basis : fixedPrice;
  const margin = basis > 0 ? Math.round(((bid - basis) / basis) * 1000) / 10 : 0;
  const quote = fixedBidQuote(bid, markupPercent);

  return (
    <div className="space-y-6" data-testid="sizing-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calculator className="size-4" aria-hidden="true" /> Sizing</CardTitle>
          <CardDescription>The scope estimate before the SOW: pick a template, set the quantities, set the items and the hours each, take lines out, read the hours and price as time and materials or a fixed bid.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {templates === null ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading templates…</div>
          ) : error ? (
            <div className="text-sm text-gray-600">{error}</div>
          ) : templates.length === 0 ? (
            <div className="text-sm text-gray-600">No template to size with yet. A system admin seeds the system templates (or adds scope items in the Template Manager); the estimate appears here as soon as one exists.</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sizing-template">Template</Label>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger id="sizing-template"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="sizing-rate">Hourly rate</Label>
                  <Input id="sizing-rate" type="number" min={0} value={hourlyRate} onChange={(e) => setHourlyRate(Number(e.target.value) || 0)} />
                  <p className="text-xs text-muted-foreground mt-1">The firm's default rate, from Firm › Settings › Scoping defaults. Change it here for this estimate only.</p>
                </div>
              </div>
              {template && (
                <SizingLinesEditor template={template} lines={lines} onChange={(id, patch) => setLines((l) => ({ ...l, [id]: { ...(l[id] ?? { quantity: 0, hoursEach: 0, included: true }), ...patch } }))} />
              )}
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${pricingModel === 'fixed_bid' ? 'items-start' : 'items-end'}`} data-testid="sizing-pricing">
                <div>
                  <Label htmlFor="sizing-pricing-model">Pricing</Label>
                  <Select value={pricingModel} onValueChange={(v) => setPricingModel(v as SizingPricingModel)}>
                    <SelectTrigger id="sizing-pricing-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="time_and_materials">Time and materials</SelectItem>
                      <SelectItem value="fixed_bid">Fixed bid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {pricingModel === 'fixed_bid' ? (
                  <>
                    <div>
                      <Label htmlFor="sizing-fixed-price">Fixed bid price</Label>
                      <Input id="sizing-fixed-price" type="number" min={0} value={fixedPrice === '' ? basis : fixedPrice} onChange={(e) => setFixedPrice(e.target.value === '' ? '' : Number(e.target.value))} />
                    </div>
                    <MarkupPercentField id="sizing-markup" value={markupPercent} onChange={setMarkupPercent} />
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground md:col-span-2">Billed as worked: the hours below at ${hourlyRate}/h, ${basis.toLocaleString()} at the estimate.</p>
                )}
              </div>
              {pricingModel === 'fixed_bid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  <p className="text-sm text-muted-foreground" data-testid="sizing-bid-margin">
                    Basis ${basis.toLocaleString()} ({Math.round(totals.hoursEstimate)} h at ${hourlyRate}/h). {margin === 0 ? 'At cost.' : margin > 0 ? `${margin}% above the basis.` : `${Math.abs(margin)}% below the basis.`}
                  </p>
                  <FixedBidQuoteLines quote={quote} testId="sizing-fixed-bid-quote" className="text-sm rounded-md border p-3" />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      {template && <ScopingCalculator scopeItems={scopeItems} hourlyRate={hourlyRate} showPricing={pricingModel === 'time_and_materials'} fixedBid={pricingModel === 'fixed_bid' ? quote : null} />}
    </div>
  );
}
